import React, { useState } from 'react';
import { Box, Typography, Popover } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

// Styled components
const BarChartContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '3px',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    overflow: 'visible',
    width: '100%',
    position: 'relative',
}));

const BarContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    marginTop: '30px',
    height: '50px',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    width: '100%',
}));

const Bar = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'clickable',
})(({ theme, height, color, clickable }) => ({
    backgroundColor: color,
    height: `${height}px`,
    borderRadius: '4px',
    width: '10px',
    flexShrink: 0,
    cursor: clickable ? 'pointer' : 'default',
    transition: 'all 0.2s ease',
}));

const TimeLabels = styled(Box)(({ theme }) => ({
    display: 'flex',
    height: '14px',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    width: '100%',
}));

const TimeLabel = styled(Typography)(({ theme }) => ({
    fontSize: '12px',
    color: theme.palette.figma.white[60],
    fontWeight: 400,
    letterSpacing: '-0.36px',
    lineHeight: 'normal',
}));

const GridLines = styled(Box)(({ theme }) => ({
    display: 'flex',
    height: '4px',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    width: '100%',
}));

const GridLine = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.figma.white[20],
    height: '4px',
    borderRadius: '2px',
    width: '1px',
    flexShrink: 0,
}));

const PopoverContent = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    padding: '5px 10px',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '5px',
    height: '27px',
    backgroundColor: theme.palette.figma.stepsDark,
    borderRadius: '10px',
}));

const BarChart = ({
    data = [],
    color = '#9ad700',
    showTimeLabels = true,
    timeLabels = ['00:00', '06:00', '12:00', '18:00'],
    maxHeight = 50,
    currentDate = null, // 현재 날짜 (오늘인지 확인용)
    clickable = false, // 바 클릭 가능 여부
    selectedHourIndex = -1, // 선택된 시간대 인덱스
    onHourSelect = null, // 시간대 선택 콜백
    sx = {},
    ...props
}) => {
    const theme = useTheme();
    const { t } = useTranslation();

    // 최대값 계산 (기본 4픽셀 포함)
    const maxValue = Math.max(...data, 1);
    const baseHeight = 4;
    const maxBarHeight = maxHeight - baseHeight;

    // 비율 계산 함수
    const calculateHeight = (value) => {
        const ratio = value / maxValue;
        return Math.round(baseHeight + (maxBarHeight * ratio));
    };

    // 시간 포맷팅 함수
    const formatTime = (hour) => {
        if (hour === 0) return `${t('am')} 12:00`;
        if (hour < 12) return `${t('am')} ${hour}:00`;
        if (hour === 12) return `${t('pm')} 12:00`;
        return `${t('pm')} ${hour - 12}:00`;
    };

    // 바의 실제 위치 계산 함수 (space-between 고려)
    const calculateBarPosition = (index) => {
        if (data.length === 1) return 50; // 바가 하나일 때 중앙

        const totalBars = data.length;

        // space-between에서 바들의 실제 위치 계산
        // 첫 번째 바: 0% + 1.67% = 1.67%
        // 마지막 바: 100% - 1.67% = 98.33%
        // 중간 바들: 1.67%와 98.33% 사이에서 선형 분배

        const barHalfWidthPercent = 1.67; // 바 너비의 절반 (1.67%)
        const startPosition = barHalfWidthPercent; // 1.67%
        const endPosition = 100 - barHalfWidthPercent; // 98.33%

        if (totalBars === 1) return 50;
        if (totalBars === 2) return index === 0 ? startPosition : endPosition;

        // 중간 바들: 1.67%와 98.33% 사이에서 선형 분배
        const range = endPosition - startPosition; // 98.33% - 1.67% = 96.66%
        const position = startPosition + (index / (totalBars - 1)) * range;

        return position;
    };

    // 오늘 날짜인지 확인하는 함수
    const isToday = () => {
        if (!currentDate) return false;

        const today = new Date();
        const targetDate = new Date(currentDate);

        return today.getFullYear() === targetDate.getFullYear() &&
            today.getMonth() === targetDate.getMonth() &&
            today.getDate() === targetDate.getDate();
    };

    // 바 클릭 핸들러
    const handleBarClick = (event, index, value) => {
        // 이미 같은 바가 선택되어 있으면 닫기
        if (selectedHourIndex === index) {
            if (onHourSelect) {
                onHourSelect(-1); // 선택 해제
            }
            return;
        }

        if (onHourSelect) {
            onHourSelect(index); // 선택된 시간대 인덱스 전달
        }
    };

    return (
        <BarChartContainer sx={sx} {...props}>
            {/* 고정된 팝업 위치 */}
            {selectedHourIndex >= 0 && (
                <Box sx={{
                    position: 'absolute',
                    top: '0px',
                    width: '100%',
                    transform: 'translateX(-50%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minWidth: '120px', // 팝업 최소 너비
                    //  maxWidth: '200px', // 팝업 최대 너비
                    // 가장자리에서 잘리지 않도록 위치 조정
                    left: selectedHourIndex < 6 ? '70px' :
                        selectedHourIndex > data.length - 6 ? '240px' :
                            `${(selectedHourIndex / (data.length - 1)) * 100}%`,
                }}>
                    {/* 팝업 내용 */}
                    <PopoverContent>
                        <Typography sx={{
                            fontSize: '12px',
                            color: theme.palette.figma.white[80],
                            fontWeight: 400,
                            textAlign: 'center',
                        }}>
                            {(() => {
                                const startHour = selectedHourIndex;
                                const endHour = selectedHourIndex + 1;

                                // 오늘 날짜일 때만 현재 시간을 기준으로 마지막 시간 판단
                                let isLastHour = false;
                                if (isToday()) {
                                    const now = new Date();
                                    const currentHour = now.getHours();
                                    isLastHour = selectedHourIndex === currentHour; // 현재 시간대
                                }

                                return isLastHour
                                    ? `${formatTime(startHour)} - ${t('now')}`
                                    : `${formatTime(startHour)} - ${formatTime(endHour)}`;
                            })()}
                        </Typography>
                        <Typography sx={{
                            fontSize: '14px',
                            color: theme.palette.figma.steps,
                            textAlign: 'center',
                            fontWeight: 500
                        }}>
                            {data[selectedHourIndex] || 0}{t('didim')}
                        </Typography>
                    </PopoverContent>
                </Box>
            )}

            <BarContainer sx={{ height: `${maxHeight}px`, zIndex: 10 }}>
                {/* 유도선 - 클릭한 바의 정확한 위치에 표시 */}
                {selectedHourIndex >= 0 && (
                    <Box sx={{
                        position: 'absolute',
                        top: '30px',
                        // 바의 실제 위치 계산 함수 사용
                        left: `${calculateBarPosition(selectedHourIndex)}%`,
                        transform: 'translateX(-50%)',
                        width: '1px',
                        height: `${maxHeight}px`,
                        backgroundColor: theme.palette.figma.steps,
                        opacity: 0.6,
                        zIndex: 5
                    }} />
                )}
                {data.map((value, index) => {
                    // 오늘 날짜인 경우 아직 오지 않은 시간대는 white30 색상 사용
                    let barColor = color;
                    let isClickable = clickable;

                    if (isToday()) {
                        const now = new Date();
                        const currentHour = now.getHours();
                        if (index > currentHour) {
                            barColor = theme.palette.figma.white[30];
                            isClickable = false; // 미래 시간대는 클릭 불가
                        }
                    }

                    return (
                        <Bar
                            key={index}
                            height={calculateHeight(value)}
                            color={barColor}
                            clickable={isClickable}
                            onClick={isClickable ? (event) => handleBarClick(event, index, value) : undefined}
                        />
                    );
                })}
            </BarContainer>
            <GridLines>
                <GridLine />
                <GridLine />
                <GridLine />
                <GridLine />
                <GridLine />
            </GridLines>
            {showTimeLabels && (
                <TimeLabels>
                    {timeLabels.map((label, index) => (
                        <TimeLabel key={index}>{label}</TimeLabel>
                    ))}
                    <TimeLabel></TimeLabel>
                </TimeLabels>
            )}
        </BarChartContainer>
    );
};

export default BarChart;
